import { useDirection } from "@radix-ui/react-direction";
import { CircleHelp, Menu } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useParticipantCountry } from "@/features/auth";
import { brandingAssetUrl } from "@/features/branding";
import { NewClaimMenu } from "@/features/payments";
import { useSupportContacts } from "@/features/support";
import { isTaxRequiredForCountry } from "@/features/tax";
import { cn } from "@/lib/utils";

import { AvatarMenu } from "./avatar-menu";
import { getNavItems, isNavItemActive } from "./nav-items";

export const AppTopNav = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const direction = useDirection();
  const { countryCode } = useParticipantCountry();
  const { hasContacts } = useSupportContacts();

  const isTaxRequired = isTaxRequiredForCountry(countryCode);
  const items = getNavItems(t, { isTaxRequired });
  const [isMobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  const closeMobile = () => setMobileOpen(false);

  const navLink = (url: string, label: string, active: boolean) => (
    <Link
      key={url}
      to={url}
      onClick={closeMobile}
      className={cn(
        "relative rounded-md px-3 py-2 text-sm transition-colors capitalize",
        active
          ? "font-bold text-foreground"
          : "font-medium text-muted-foreground hover:text-foreground"
      )}
    >
      {label}
    </Link>
  );

  return (
    <header className="sticky top-0 z-40 bg-background pt-4 md:pt-6 lg:static">
      <div className="mx-auto w-full max-w-(--breakpoint-xl) px-4 md:px-8 flex min-h-12 items-center gap-x-4 md:min-h-16">
        <Link to="/" className="shrink-0">
          <img
            alt="logo"
            className="h-[25px] max-w-[150px] w-auto object-contain cursor-pointer"
            src={brandingAssetUrl("logo")}
          />
        </Link>

        <div className="flex-1" />

        <nav className="hidden items-center gap-x-6 lg:flex">
          {items.map((item) =>
            navLink(item.url, item.label, isNavItemActive(item, pathname))
          )}
        </nav>

        <div className="hidden items-center gap-x-3 lg:flex">
          {hasContacts && (
            <Button variant="outline" onClick={() => navigate("/support")}>
              {t("tabBar_support")}
            </Button>
          )}
          <NewClaimMenu />
          <AvatarMenu />
        </div>

        <div className="flex items-center gap-x-2 lg:hidden">
          <AvatarMenu />
          <Sheet open={isMobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button
                type="button"
                aria-label="Open menu"
                className="text-foreground hover:text-foreground/85"
              >
                <Menu className="size-6" />
              </button>
            </SheetTrigger>
            <SheetContent side={direction === "rtl" ? "left" : "right"}>
              <SheetHeader>
                <SheetTitle className="text-left">
                  <img
                    alt="logo"
                    className="h-10"
                    src={brandingAssetUrl("logo")}
                  />
                </SheetTitle>
              </SheetHeader>
              <nav className="mt-6 flex flex-col gap-y-1">
                {items.map((item) =>
                  navLink(item.url, item.label, isNavItemActive(item, pathname))
                )}
              </nav>
              <div className="mt-6 flex flex-col gap-y-2">
                {hasContacts && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      closeMobile();
                      navigate("/support");
                    }}
                  >
                    <CircleHelp className="mr-1 h-4 w-4" />
                    {t("tabBar_support")}
                  </Button>
                )}
                <NewClaimMenu onBeforeNavigate={closeMobile} />
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
